import i18next from "i18next";
import { z } from "zod";

import { PaymentEventStatus } from "@/apis/gql/types";
import { ibanSchema } from "@/lib/validations/iban";

export const paymentSchema = z.object({
  bankTxnId: z.string(),
  recipientName: z.string(),
  recipientIban: z.string().optional().nullable(),
  purpose: z.string().trim().min(1).max(140).optional().nullable(),
});

const validateIbanMatch = (
  recipientIban: string,
  supplierIban: string,
  ctx: z.RefinementCtx,
  path: (string | number)[] = ["iban"],
) => {
  if (recipientIban.replaceAll(" ", "") !== supplierIban.replaceAll(" ", "")) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: i18next.t("validation.payment.different_iban", {
        iban: supplierIban,
      }),
      path,
    });
  }
};

export const paymentWarningsSchema = z
  .object({
    supplier: z.object({
      paymentDetails: z.object({
        IBAN: z.string().nullable(),
      }),
    }),
    payments: z.array(
      z.object({
        status: z.nativeEnum(PaymentEventStatus),
        recipientIban: ibanSchema,
        purpose: z.string().trim().min(1).max(140).optional().nullable(),
      }),
    ),
  })
  .superRefine((data, ctx) => {
    const supplierIban = data.supplier.paymentDetails.IBAN;
    if (!supplierIban) return;

    const activePayments = data.payments.filter((p) =>
      [PaymentEventStatus.DRAFT, PaymentEventStatus.CONFIRMED].includes(
        p.status,
      ),
    );

    activePayments.forEach((payment) => {
      validateIbanMatch(payment.recipientIban, supplierIban, ctx);
    });
  });

export const ibanValidationSchema = z
  .object({
    supplier: z.object({
      paymentDetails: z.object({
        IBAN: z.string().optional().nullable(),
      }),
    }),
    iban: ibanSchema,
  })
  .superRefine((data, ctx) => {
    if (!data.iban || !data.supplier.paymentDetails.IBAN) return;
    validateIbanMatch(data.iban, data.supplier.paymentDetails.IBAN, ctx);
  });

export const paymentValidationSchema = z.object({
  bankTxnId: z.string(),
  recipientName: z.string(),
  recipientIban: z.string(),
  purpose: z.string().min(1).max(140).optional().nullable(),
});
